<template>
  <v-row class="app-preview">
    <v-col
      cols="12"
      md="9"
    >
      <v-card class="app-lead-editable">
        <!-- Header -->
        <div class="py-5 px-5">
          <Letterhead :width="'100%'" />

          <div class="d-flex flex-wrap justify-space-between mt-9">
            <!-- Date -->
            <div class="d-flex align-center">
              <span class="me-2 font-weight-medium text--primary">Date Issued: {{ dateToday }}</span>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Customer Details -->
        <div class="py-5 px-5">
          <div class="mb-3 d-flex">
            <div class="px-3 flex-grow-1 font-weight-semibold">
              <v-row>
                <!-- Customer Name -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pl-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Quoted To:
                  </p>
                  <v-text-field
                    v-model="lead.name"
                    single-line
                    dense
                    outlined
                    hide-details
                    placeholder="Customer Name (Optional)"
                  ></v-text-field>
                </v-col>

                <!-- Contact Number -->
                <v-col
                  cols="12"
                  sm="6"
                  :class="{ 'pr-0 mb-6 mb-sm-0': $vuetify.breakpoint.smAndUp, 'px-0': $vuetify.breakpoint.xsOnly }"
                >
                  <p class="font-weight-semibold payment-details-header">
                    Contact No.:
                  </p>
                  <v-text-field
                    v-model="lead.contact_number"
                    single-line
                    dense
                    outlined
                    type="number"
                    :rules="[v => /[0-9\-().\s]{7}$/.test(v) || 'Must be valid contact number']"
                    hide-details="auto"
                    placeholder="Contact Number"
                    prefix="+673"
                    @mousewheel.native.prevent
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Lead Details -->
        <div class="py-5 px-5">
          <div class="mb-3 d-none d-md-flex">
            <div class="px-3 flex-grow-1 font-weight-semibold">
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <span>Description</span>
                </v-col>
              </v-row>
            </div>
            <div class="header-spacer"></div>
          </div>

          <!-- Insurance Product Type -->
          <v-card
            outlined
            class="d-flex pa-3"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.insurance_product_id"
                  :items="insuranceProductOptions"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Select Insurance Product Type"
                  @change="clearRoadTaxInfo()"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Car Registration Number -->
          <v-card
            v-if="isMotorInsurance"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="lead.car_registration_number"
                  single-line
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Car Registration Number"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <!-- Insurance -->
          <v-card
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.insurance_id"
                  :items="insuranceOptions"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Select Insurance"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Customer self service -->
          <v-card
            v-if="isMotorInsurance"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.roadtax_self_service"
                  :items="[{ id: true, name: 'Yes' }, { id: false, name: 'No' }]"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Customer self service?"
                  :label="lead.roadtax_self_service == null ? '' : 'Customer self service?'"
                  @change="updateRoadTaxInfo($event)"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Pickup Location -->
          <v-card
            v-if="isMotorInsurance && lead.roadtax_self_service === false"
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-select
                  v-model="lead.pickup_location_id"
                  :items="pickupLocationOptions"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  hide-details="auto"
                  placeholder="Select Road Tax Pickup Location"
                ></v-select>
              </v-col>
            </v-row>
          </v-card>

          <!-- Previous Policy -->
          <v-card
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-file-input
                  prepend-icon=""
                  :append-icon="icons.mdiPaperclip"
                  accept="application/pdf, image/png, image/jpg, image/jpeg"
                  show-size
                  dense
                  outlined
                  :rules="policyUploadRules"
                  label="Upload previous policy"
                  hide-details="auto"
                  @change="directUploadPolicy($event)"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip
                      small
                      label
                      color="primary"
                    >
                      {{ text }}
                    </v-chip>
                  </template>

                  <template v-slot:append>
                    <v-progress-circular
                      v-if="uploadLoading"
                      color="primary"
                      indeterminate
                      size="25"
                    ></v-progress-circular>
                  </template>
                </v-file-input>
              </v-col>
            </v-row>
          </v-card>

          <!-- Remarks -->
          <v-card
            outlined
            class="d-flex pa-3 mt-5"
          >
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-textarea
                  v-model="lead.remarks"
                  dense
                  outlined
                  rows="3"
                  hide-details="auto"
                  placeholder="Remarks"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-card>
        </div>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      md="3"
    >
      <v-card class="mb-6">
        <v-card-text>
          <v-btn
            color="primary"
            block
            :loading="loading"
            :disabled="loading"
            @click="createLead"
          >
            Confirm Lead
          </v-btn>

          <div class="text-center mt-4">
            {{ t('Please ensure that all fields are filled correctly as this record will not be editable after confirming.') }}
          </div>
        </v-card-text>
      </v-card>
      <v-card
        v-if="errors.length != 0"
        class="mb-6"
      >
        <v-card-text>
          <Alert
            :messages="errors"
            color="error"
          ></Alert>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mdiCalendar, mdiPaperclip } from '@mdi/js'
import {
  inject,
  onMounted,
  onUnmounted,
  computed,
  ref,
} from '@vue/composition-api'
import { useRouter } from '@core/utils'
import { Letterhead } from '@/components/apps/previews'
import Alert from '@/components/Alert'

// import notificationService from '@/services/notification.service'
import leadStoreModule from './leadStoreModule'
import receiptStoreModule from '../receipt/receiptStoreModule'

export default {
  components: {
    Alert,
    Letterhead,
  },
  setup() {
    // Initializers
    const store = inject('store')
    const snackbarService = inject('snackbarService')
    const t = inject('t')
    const { router } = useRouter()
    const LEAD_APP_STORE_MODULE_NAME = 'app-lead'
    const RECEIPT_APP_STORE_MODULE_NAME = 'app-receipt'

    if (!store.hasModule(LEAD_APP_STORE_MODULE_NAME)) {
      store.registerModule(LEAD_APP_STORE_MODULE_NAME, leadStoreModule)
    }

    if (!store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) {
      store.registerModule(RECEIPT_APP_STORE_MODULE_NAME, receiptStoreModule)
    }

    // Properties
    const lead = ref({
      name: null,
      contact_number: null,
      insurance_product_id: null,
      insurance_id: null,
      car_registration_number: null,
      roadtax_self_service: null,
      require_inspection: null,
      pickup_location_id: null,
      previous_policy: null,
      remarks: null,
      no_claim_discount: null,
      period: null,
      sum_insured: null,
      after_discount: null,
      road_tax: null,
      additional_fees: null,
    })
    const errors = ref([])
    const userData = JSON.parse(localStorage.getItem('userData'))
    const dateToday = new Date().toLocaleDateString('en-SG')
    const loading = ref(false)
    const uploadLoading = ref(false)
    const policyUploadRules = [value => !value || value.size < 5000000 || 'Policy file has to be lower than 5MB']
    const insuranceProductOptions = ref([])
    const insuranceOptions = ref([])
    const pickupLocationOptions = ref([])
    const motorInsuranceId = ref(null)

    // const appCable = notificationService.webSocketConnection()

    // Methods
    const fetchInsuranceProducts = () => {
      store
        .dispatch('app-receipt/fetchInsuranceProducts')
        .then(response => {
          const { data } = response.data
          insuranceProductOptions.value = data
          motorInsuranceId.value = insuranceProductOptions.value.filter(product => product.name === 'Motor insurance')[0].id
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching insurance products. Please refresh!')
        })
    }
    const fetchInsurances = () => {
      store
        .dispatch('app-receipt/fetchInsurances')
        .then(response => {
          const { data } = response.data
          insuranceOptions.value = data
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching insurances. Please refresh!')
        })
    }
    const fetchPickupLocations = () => {
      store
        .dispatch('app-receipt/fetchPickupLocations')
        .then(response => {
          const { data } = response.data
          pickupLocationOptions.value = data
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching pickup location. Please refresh!')
        })
    }
    const createLead = () => {
      loading.value = true
      errors.value = []
      lead.value.name = lead.value.name ? lead.value.name : ''
      store
        .dispatch('app-lead/createLead', { lead: lead.value })
        .then(() => {
          router.push('/apps/leads/new/list')
          loading.value = false
        })
        .catch(error => {
          loading.value = false
          errors.value = error.response.data.errors
          snackbarService.error(error.response.data.message || 'Something went wrong while saving lead. Please refresh!')
        })
    }
    const directUploadPolicy = file => {
      if (file == null) {
        lead.value.previous_policy = null
        lead.value.previous_policy_name = null

        return
      }

      uploadLoading.value = true
      store
        .dispatch('app-lead/directUploadPolicy', file)
        .then(response => {
          uploadLoading.value = false
          if (response) {
            lead.value.previous_policy = response.signed_id
            lead.value.previous_policy_name = response.filename
          }
        })
        .catch(error => {
          uploadLoading.value = false
          snackbarService.error(error.response.data.message || 'Something went wrong while uploading policy. Please refresh!')
        })
    }
    const clearRoadTaxInfo = () => {
      lead.value.car_registration_number = null
      lead.value.road_tax = null
      lead.value.pickup_location_id = null
    }
    const updateRoadTaxInfo = event => {
      if (event) {
        lead.value.pickup_location_id = null
      }
    }

    /*
    const fetchLeadCounts = () => {
      store
        .dispatch('fetchLeadCounts')
        .then(response => {
          const { data } = response.data
          store.dispatch('setLeadCounts', data)
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching lead counts. Please refresh!')
        })
    }
    */

    /*
    const updateLeads = () => {
      appCable.subscriptions.create({
        channel: 'LeadChannel',
      },
      {
        connected() {
          console.log('Connected to the leads channel')
        },
        received(data) {
          switch (data.event) {
            case 'new_lead':
              fetchLeadCounts()
              break
            case 'quoted_lead':
              fetchLeadCounts()
              break
            case 'refresh_leads':
              fetchLeadCounts()
              break
            case 'issue_lead':
              fetchLeadCounts()
              break
            default:
          }
        },
        disconnected() {
          console.log('Disconnected from the leads channel')
        },
      })
    }
    */

    // Computed
    const isMotorInsurance = computed(() => (motorInsuranceId.value === lead.value.insurance_product_id))

    // Mounted
    onMounted(() => {
      fetchInsuranceProducts()
      fetchInsurances()
      fetchPickupLocations()

      // updateLeads()
    })

    // Unmounted
    onUnmounted(() => {
      if (store.hasModule(LEAD_APP_STORE_MODULE_NAME)) store.unregisterModule(LEAD_APP_STORE_MODULE_NAME)
      if (store.hasModule(RECEIPT_APP_STORE_MODULE_NAME)) store.unregisterModule(RECEIPT_APP_STORE_MODULE_NAME)
    })

    return {
      t,
      lead,
      errors,
      userData,
      dateToday,
      loading,
      createLead,
      directUploadPolicy,
      uploadLoading,
      policyUploadRules,
      insuranceProductOptions,
      pickupLocationOptions,
      motorInsuranceId,
      clearRoadTaxInfo,
      isMotorInsurance,
      updateRoadTaxInfo,
      insuranceOptions,

      icons: {
        mdiCalendar,
        mdiPaperclip,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/apps/style.scss';

.app-lead-editable {
  .lead-add-branch {
    width: 15rem;
  }
}
</style>
